"use client";

import { useQuery } from "@tanstack/react-query";
import { Building2, Check, Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Input } from "@/components/ui/input";
import { apiClient } from "@/lib/api-client";
import { PublicSchool } from "@/lib/types";
import { cn } from "@/lib/utils";

interface Props {
  value: string;
  onChange: (code: string) => void;
}

/** School code/name lookup for the Guard login screen. The code is only a
 * routing hint client-side - the server independently verifies the guard
 * actually belongs to the resolved school. */
export function SchoolSearchInput({ value, onChange }: Props) {
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: results } = useQuery({
    queryKey: ["school-search", query],
    queryFn: async () => (await apiClient.get<PublicSchool[]>("/auth/schools/search", { params: { q: query } })).data,
    enabled: query.length >= 2,
  });

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="School code or name (e.g. DPS)"
          className="pl-8"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
      </div>
      {open && query.length >= 2 && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border bg-popover shadow-md">
          {results && results.length > 0 ? (
            results.map((school) => (
              <button
                key={school.id}
                type="button"
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent",
                  school.code === value && "bg-accent"
                )}
                onClick={() => {
                  setQuery(school.code);
                  onChange(school.code);
                  setOpen(false);
                }}
              >
                <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate">
                  {school.name} <span className="text-muted-foreground">· {school.code}</span>
                </span>
                {school.code === value && <Check className="h-4 w-4 shrink-0 text-primary" />}
              </button>
            ))
          ) : (
            <p className="px-3 py-2 text-sm text-muted-foreground">No schools found</p>
          )}
        </div>
      )}
    </div>
  );
}
